import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  FlexBox,
  FormDialog,
  Grid,
  Icon,
  MuiBox,
  SelectDropdown,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as ciActions, selectors as ciSelectors, ClientIntegration } from 'store/clientIntegrations';
import { actions as clientActions, selectors as clientsSelectors } from 'store/clients';
import { actions as settingsActions, selectors as settingsSelectors } from 'store/settings';
import { getSortedList } from 'utils/stringFormat';
import { useStyles } from '../styled';
import ManageEnvironmentsOverlay from './components/ManageEnvironmentsOverlay';

const EnvironmentsView = () => {
  const classes = useStyles();

  // ACTIONS AND SELECTORS
  const { resetClientAction, invalidateCache, resetCIAction, setClientIntegrationId } = useActions({
    resetClientAction: clientActions.resetAction,
    invalidateCache: clientActions.invalidateCache,
    resetCIAction: ciActions.resetAction,
    setClientIntegrationId: settingsActions.setClientIntegrationId
  });
  const clientAction = useSelector(clientsSelectors.selectAction);
  const environments = useSelector(ciSelectors.selectClientIntegrations);
  const sortedEnvironments = useMemo(() => getSortedList(environments, 'name'), [environments]);
  const clientIntegrationId = useSelector(settingsSelectors.selectClientIntegrationId);
  const ciAction = useSelector(ciSelectors.selectAction);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showManageOverlay, setShowManageOverlay] = useState(false);

  // Fall back to the first environment when none is selected or the selected one no longer exists
  const selectedEnvironment =
    sortedEnvironments.find((e) => e.id === clientIntegrationId) || sortedEnvironments[0] || null;

  useEffect(() => {
    if (sortedEnvironments.length && !clientIntegrationId) {
      setClientIntegrationId(sortedEnvironments[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedEnvironments]);

  // Handle API errors
  useEffect(() => {
    if (clientAction.error) {
      setErrorMessage(clientAction.error);
      resetClientAction();
    }
  }, [clientAction.error]);

  useEffect(() => {
    if (ciAction.success) {
      resetCIAction();
    }
  }, [ciAction.success]);

  useEffect(() => {
    if (ciAction.error) {
      // Errors with a known code have a specific message; everything else gets the generic one
      setErrorMessage(ciAction.errorCode ? ciAction.error : 'Uh oh! Something went wrong. Please try again.');
      resetCIAction();
    }
  }, [ciAction.error]);

  return (
    <>
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage('')}
          confirmButtonAction={() => setErrorMessage('')}
          confirmButtonText="Ok"
          open={Boolean(errorMessage)}
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
      {/* Header */}
      <Grid className={classes.headerBlock}>
        <Box flexOverride="none">
          <Typography className={classes.headerText}>Environments</Typography>
        </Box>
        {/* Environment selection */}
        {/* Label and gear sit beside the dropdown, or above it on smaller screens */}
        <MuiBox
          display="grid"
          alignItems="center"
          gap="10px"
          sx={{
            gridTemplateAreas: { xs: '"label gear" "dropdown dropdown"', md: '"label dropdown gear"' },
            gridTemplateColumns: { xs: '1fr auto', md: 'auto auto auto' }
          }}
        >
          <Typography
            sx={{ gridArea: 'label', fontSize: '16px', fontWeight: 'bold', paddingLeft: { xs: '16px', md: 0 } }}
          >
            Environment
          </Typography>
          <MuiBox gridArea="dropdown">
            <SelectDropdown
              items={sortedEnvironments}
              itemToString={(item: ClientIntegration) => item && item.name}
              selectedItem={selectedEnvironment as ClientIntegration}
              onChange={(item: ClientIntegration) => setClientIntegrationId(item.id)}
              radius="round"
              width="262px"
              sx={{
                boxShadow: 'none',
                height: '45px',
                '& .MuiSelect-select': {
                  color: selectedEnvironment ? '#000' : '#9E9E9E'
                }
              }}
              data-testid="environment-selector"
            />
          </MuiBox>
          <MuiBox gridArea="gear" display="flex" marginRight={{ xs: '10px', md: 0 }}>
            <Tooltip title="Manage Environments" arrow placement="top" verticalOffset={-8}>
              <Icon
                name="Settings"
                sx={{ cursor: 'pointer', display: 'block' }}
                onClick={() => setShowManageOverlay(true)}
                data-testid="manage-environments-button"
              />
            </Tooltip>
          </MuiBox>
        </MuiBox>
      </Grid>
      {/* Clear cache button */}
      <FlexBox padding="0 7 7" align="center">
        <Typography className={classes.fieldLabel}>Clear cached event data</Typography>
        <Button
          customSize="small"
          isLoading={clientAction.pending}
          onClick={invalidateCache}
          sx={{ margin: '0px 10px' }}
        >
          Clear Cache
        </Button>
      </FlexBox>
      {showManageOverlay && <ManageEnvironmentsOverlay onClose={() => setShowManageOverlay(false)} />}
    </>
  );
};

export default EnvironmentsView;
